<template>
    <div class="gwp-profile-manager">
        <div class="tab-content">
            <div class="table-container-compact">
                <table class="table-compact">
                    <thead>
                        <tr>
                            <th class="text-center">코드</th>
                            <th class="text-center">이름</th>
                            <th class="text-center">GWP 버전</th>
                            <th class="text-center">시간지평</th>
                            <th class="text-center">기본</th>
                            <th class="text-center">상태</th>
                            <th class="text-center"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="7" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중</td>
                        </tr>
                        <tr v-else-if="profiles.length === 0">
                            <td colspan="7" class="text-center empty-state">
                                <i class="fas fa-temperature-high"></i>
                                <p>등록된 GWP 프로파일이 없습니다</p>
                            </td>
                        </tr>
                        <tr
                            v-else
                            v-for="p in profiles"
                            :key="p.profileIdx"
                            class="clickable-row"
                            :class="{ 'inactive-row': p.draftStat === 'INACTIVE' }"
                            @click="onProfileRowClick(p, $event)"
                        >
                            <td class="text-center">
                                <strong class="profile-code-cell">{{ p.profileCode }}</strong>
                            </td>
                            <td class="text-center">{{ p.profileName }}</td>
                            <td class="text-center">{{ getGwpVersionLabel(p.gwpVersion) }}</td>
                            <td class="text-center">{{ getHorizonLabel(p.horizon) }}</td>
                            <td class="text-center">{{ p.isDefault ? "기본" : "-" }}</td>
                            <td class="text-center">{{ getDraftStatLabel(p.draftStat) }}</td>
                            <td>
                                <div class="action-buttons text-center" @click.stop>
                                    <IconButton icon="fas fa-list" title="GWP 값 관리" @click="openValuesModal(p)" />
                                    <IconButton icon="fas fa-edit" title="수정" @click="openProfileModal('edit', p)" />
                                    <IconButton
                                        icon="fas fa-trash"
                                        variant="delete"
                                        title="삭제"
                                        :disabled="p.isDefault"
                                        @click="deleteProfile(p)"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 프로파일 상세/생성/수정 모달 -->
        <b-modal v-model="showProfileModal" :title="profileModalTitle" size="md" centered>
            <form @submit.prevent="profileModalMode !== 'view' && saveProfile()">
                <div class="form-group">
                    <label>프로파일 코드 <span v-if="profileModalMode !== 'view'" class="required">*</span></label>
                    <input
                        v-model="profileForm.profileCode"
                        type="text"
                        class="form-control"
                        placeholder="예: AR5-100"
                        :readonly="profileModalMode === 'edit' || profileModalMode === 'view'"
                        :required="profileModalMode !== 'view'"
                    />
                </div>
                <div class="form-group">
                    <label>프로파일명 <span v-if="profileModalMode !== 'view'" class="required">*</span></label>
                    <input
                        v-model="profileForm.profileName"
                        type="text"
                        class="form-control"
                        placeholder="예: IPCC AR5 (100년)"
                        :readonly="profileModalMode === 'view'"
                        :required="profileModalMode !== 'view'"
                    />
                </div>
                <div class="form-group">
                    <label>GWP 버전</label>
                    <select
                        v-model="profileForm.gwpVersion"
                        class="form-control"
                        :disabled="profileModalMode === 'view'"
                    >
                        <option v-for="opt in gwpVersionOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>상태</label>
                    <select
                        v-model="profileForm.draftStat"
                        class="form-control"
                        :disabled="profileModalMode === 'view'"
                    >
                        <option v-for="opt in draftStatOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>시간지평 (년)</label>
                    <select v-model="profileForm.horizon" class="form-control" :disabled="profileModalMode === 'view'">
                        <option v-for="opt in horizonOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label class="checkbox-label">
                        <input
                            v-model="profileForm.isDefault"
                            type="checkbox"
                            :disabled="profileModalMode === 'view'"
                        />
                        기본 프로파일로 설정
                    </label>
                </div>

                <div class="form-group">
                    <label>비고</label>
                    <input
                        v-model="profileForm.remarks"
                        type="text"
                        class="form-control"
                        placeholder="선택"
                        :readonly="profileModalMode === 'view'"
                    />
                </div>
            </form>
            <template #modal-footer>
                <template v-if="profileModalMode === 'view'">
                    <button type="button" class="btn btn-secondary" @click="showProfileModal = false">닫기</button>
                </template>
                <template v-else>
                    <button type="button" class="btn btn-secondary" @click="showProfileModal = false">취소</button>
                    <button type="button" class="btn btn-primary" @click="saveProfile">저장</button>
                </template>
            </template>
        </b-modal>

        <!-- GWP 값 관리 모달 -->
        <b-modal v-model="showValuesModal" title="GWP 값 관리" size="lg" centered>
            <template #modal-header="{ close }">
                <div class="modal-detail-header">
                    <h5 class="modal-title">GWP 값 관리</h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            </template>
            <div v-if="selectedProfile" class="values-panel">
                <div class="values-bar">
                    <span class="values-profile-label"
                        >{{ selectedProfile.profileName }} ({{ selectedProfile.profileCode }})</span
                    >
                    <CarbonButton icon-only icon="fas fa-plus" title="값 추가" @click="openValueForm()" />
                </div>
                <div class="table-container-compact values-table-wrapper">
                    <table class="table-compact">
                        <thead>
                            <tr>
                                <th class="text-center">가스</th>
                                <th class="text-center">GWP 값</th>
                                <th class="text-center">출처</th>
                                <th class="text-center"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loadingValues">
                                <td colspan="4" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중</td>
                            </tr>
                            <tr v-else-if="values.length === 0">
                                <td colspan="4" class="text-center empty-state">
                                    <i class="fas fa-list"></i>
                                    <p>등록된 GWP 값이 없습니다</p>
                                </td>
                            </tr>
                            <tr v-else v-for="v in values" :key="v.gwpValueIdx">
                                <td class="text-center">{{ getGasLabel(v.gasCode) }} ({{ v.gasCode }})</td>
                                <td class="text-center">{{ v.gwpValue }}</td>
                                <td class="text-center">{{ getSourceRefLabel(v.sourceRef) }}</td>
                                <td>
                                    <div class="action-buttons text-center">
                                        <IconButton icon="fas fa-edit" title="수정" @click="openValueForm(v)" />
                                        <IconButton
                                            icon="fas fa-trash"
                                            variant="delete"
                                            title="삭제"
                                            @click="deleteValue(v)"
                                        />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <template #modal-footer>
                <button type="button" class="btn btn-secondary" @click="showValuesModal = false">닫기</button>
            </template>
        </b-modal>

        <!-- GWP 값 추가/수정 모달 -->
        <b-modal
            v-model="showValueFormModal"
            :title="editingValue ? 'GWP 값 수정' : 'GWP 값 추가'"
            size="md"
            centered
            @hide="cancelValueForm"
        >
            <form @submit.prevent="saveValue">
                <div class="form-group">
                    <label>가스 <span class="required">*</span></label>
                    <select v-model="valueForm.gasCode" class="form-control" required :disabled="!!editingValue">
                        <option value="">선택</option>
                        <option v-for="opt in gasCodeOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }} ({{ opt.value }})
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label>GWP 값 <span class="required">*</span></label>
                    <input
                        v-model.number="valueForm.gwpValue"
                        type="number"
                        step="0.000001"
                        class="form-control"
                        required
                    />
                </div>
                <div class="form-group">
                    <label>출처</label>
                    <select v-model="valueForm.sourceRef" class="form-control">
                        <option value="">선택</option>
                        <option v-for="opt in sourceRefOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>
            </form>
            <template #modal-footer>
                <button type="button" class="btn btn-secondary" @click="cancelValueForm">취소</button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="saveValue"
                    :disabled="!valueForm.gasCode || valueForm.gwpValue == null"
                >
                    저장
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import carbonApi from "../../api";
import CarbonCodes from "../../codes";
import IconButton from "../common/IconButton.vue";
import CarbonButton from "../common/CarbonButton.vue";

export default {
    name: "GwpProfileMain",
    components: { IconButton, CarbonButton },
    props: {},
    data() {
        return {
            profiles: [],
            loading: false,
            showProfileModal: false,
            profileModalMode: "create",
            profileForm: {
                profileCode: "",
                profileName: "",
                gwpVersion: "AR5",
                horizon: 100,
                draftStat: "DRAFT",
                isDefault: false,
                remarks: "",
            },
            editingProfileIdx: null,
            showValuesModal: false,
            selectedProfile: null,
            values: [],
            loadingValues: false,
            showValueFormModal: false,
            valueForm: { gasCode: "", gwpValue: null, sourceRef: "" },
            editingValue: null,
        };
    },
    computed: {
        gwpVersionOptions() {
            const list = CarbonCodes.gwpVersion?.getList?.() ?? [];
            if (list.length) return list.map((c) => ({ value: c.value, text: c.text }));
            return [
                { value: "AR2", text: "AR2" },
                { value: "AR3", text: "AR3" },
                { value: "AR4", text: "AR4" },
                { value: "AR5", text: "AR5" },
                { value: "AR6", text: "AR6" },
            ];
        },
        draftStatOptions() {
            const list = CarbonCodes.draftStat?.getList?.() ?? [];
            if (list.length) return list.map((c) => ({ value: c.value, text: c.text }));
            return [
                { value: "DRAFT", text: "초안" },
                { value: "ACTIVE", text: "활성" },
                { value: "INACTIVE", text: "비활성" },
            ];
        },
        horizonOptions() {
            const list = CarbonCodes.horizon?.getList?.() ?? [];
            const toNum = (v) => {
                if (v === "H20" || v === 20) return 20;
                if (v === "H100" || v === 100) return 100;
                if (v === "H500" || v === 500) return 500;
                return Number(v) || 100;
            };
            if (list.length) return list.map((c) => ({ value: toNum(c.value), text: c.text }));
            return [
                { value: 20, text: "20년" },
                { value: 100, text: "100년" },
                { value: 500, text: "500년" },
            ];
        },
        gasCodeOptions() {
            const list = CarbonCodes.gasCode?.getList?.() ?? [];
            if (list.length) return list.map((c) => ({ value: c.value, text: c.text }));
            return [
                { value: "CO2", text: "CO2" },
                { value: "CH4", text: "CH4" },
                { value: "N2O", text: "N2O" },
                { value: "HFCs", text: "HFCs" },
                { value: "PFCs", text: "PFCs" },
                { value: "SF6", text: "SF6" },
                { value: "NF3", text: "NF3" },
                { value: "OTHER", text: "OTHER" },
            ];
        },
        sourceRefOptions() {
            const list = CarbonCodes.sourceRef?.getList?.() ?? [];
            if (list.length) return list.map((c) => ({ value: c.value, text: c.text }));
            return [
                { value: "IPCC_AR4", text: "IPCC AR4" },
                { value: "IPCC_AR5", text: "IPCC AR5" },
                { value: "IPCC_AR6", text: "IPCC AR6" },
                { value: "NATIONAL_GUIDE", text: "국가 가이드" },
                { value: "OTHER", text: "기타" },
            ];
        },
        profileModalTitle() {
            if (this.profileModalMode === "view") return "GWP 프로파일 상세";
            if (this.profileModalMode === "create") return "신규 GWP 프로파일";
            return "GWP 프로파일 수정";
        },
    },
    mounted() {
        this.loadProfiles();
    },
    methods: {
        async loadProfiles() {
            this.loading = true;
            try {
                const res = await carbonApi.gwpProfile.getProfiles();
                const data = res?.data?.data ?? res?.data ?? res;
                this.profiles = Array.isArray(data) ? data : (data?.profiles ?? data?.rows ?? []);
            } catch (e) {
                console.error("GWP 프로파일 조회 실패:", e);
                this.$bvToast?.toast?.(e?.response?.data?.message ?? "GWP 프로파일 조회에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
                this.profiles = [];
            } finally {
                this.loading = false;
            }
        },
        getGwpVersionLabel(v) {
            return CarbonCodes.gwpVersion?.getLabel?.(v) ?? v ?? "-";
        },
        getDraftStatLabel(v) {
            return CarbonCodes.draftStat?.getLabel?.(v) ?? v ?? "-";
        },
        getGasLabel(v) {
            return CarbonCodes.gasCode?.getLabel?.(v) ?? v ?? "-";
        },
        getSourceRefLabel(v) {
            return v ? (CarbonCodes.sourceRef?.getLabel?.(v) ?? v) : "-";
        },
        getHorizonLabel(v) {
            if (v == null || v === "") return "-";
            const code =
                v === 20 || v === "20" || v === "H20"
                    ? "H20"
                    : v === 100 || v === "100" || v === "H100"
                      ? "H100"
                      : v === 500 || v === "500" || v === "H500"
                        ? "H500"
                        : null;
            return code ? (CarbonCodes.horizon?.getLabel?.(code) ?? `${v}년`) : `${v}년`;
        },
        onProfileRowClick(profile, event) {
            if (event.target.closest(".action-buttons")) return;
            this.openProfileModal("view", profile);
        },
        openProfileModal(mode, profile = null) {
            this.profileModalMode = mode;
            this.editingProfileIdx = profile?.profileIdx ?? null;
            this.profileForm = {
                draftStat: profile?.draftStat ?? "DRAFT",
                profileCode: profile?.profileCode ?? "",
                profileName: profile?.profileName ?? "",
                gwpVersion: profile?.gwpVersion ?? "AR5",
                horizon: profile?.horizon ?? 100,
                isDefault: profile?.isDefault ?? false,
                remarks: profile?.remarks ?? "",
            };
            this.showProfileModal = true;
        },
        async saveProfile() {
            if (this.profileModalMode === "view") return;
            try {
                if (this.profileModalMode === "create") {
                    const res = await carbonApi.gwpProfile.createProfile(this.profileForm);
                    if (res.status === 200) {
                        this.$bvToast?.toast?.("GWP 프로파일이 생성되었습니다.", { variant: "success" });
                        this.showProfileModal = false;
                        this.loadProfiles();
                    } else {
                        const msg = res?.data?.error?.message ?? "GWP 프로파일 생성에 실패했습니다.";
                        this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
                    }
                } else {
                    const res = await carbonApi.gwpProfile.updateProfile(this.editingProfileIdx, this.profileForm);
                    if (res.status === 200) {
                        this.$bvToast?.toast?.("GWP 프로파일이 수정되었습니다.", { variant: "success" });
                        this.showProfileModal = false;
                        this.loadProfiles();
                    } else {
                        const msg = res?.data?.error?.message ?? "GWP 프로파일 수정에 실패했습니다.";
                        this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
                    }
                }
            } catch (e) {
                console.error("GWP 프로파일 저장 실패:", e);
            }
        },
        async deleteProfile(profile) {
            const confirm = await this.alertConfirmDanger(`"${profile.profileName}" 프로파일을 삭제하시겠습니까?`);
            if (!confirm.value) return;
            try {
                const res = await carbonApi.gwpProfile.deleteProfile(profile.profileIdx);
                if (res.status === 200) {
                    this.$bvToast?.toast?.("GWP 프로파일이 삭제되었습니다.", { variant: "success" });
                    this.loadProfiles();
                } else {
                    const msg = res?.data?.error?.message ?? "GWP 프로파일 삭제에 실패했습니다.";
                    this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
                }
            } catch (e) {
                console.error("GWP 프로파일 삭제 실패:", e);
            }
        },
        async openValuesModal(profile) {
            this.selectedProfile = profile;
            this.values = [];
            this.showValueFormModal = false;
            this.editingValue = null;
            this.showValuesModal = true;
            await this.loadValues();
        },
        async loadValues() {
            if (!this.selectedProfile) return;
            this.loadingValues = true;
            try {
                const res = await carbonApi.gwpProfile.getValues(this.selectedProfile.profileIdx);
                const data = res?.data?.data ?? res?.data ?? res;
                this.values = Array.isArray(data) ? data : (data?.values ?? data?.rows ?? []);
            } catch (e) {
                console.error("GWP 값 조회 실패:", e);
                this.values = [];
            } finally {
                this.loadingValues = false;
            }
        },
        openValueForm(value = null) {
            this.editingValue = value;
            this.valueForm = {
                gasCode: value?.gasCode ?? "",
                gwpValue: value?.gwpValue ?? null,
                sourceRef: value?.sourceRef ?? "",
            };
            this.showValueFormModal = true;
        },
        cancelValueForm() {
            this.showValueFormModal = false;
            this.editingValue = null;
        },
        async saveValue() {
            if (!this.selectedProfile || !this.valueForm.gasCode || this.valueForm.gwpValue == null) return;
            try {
                if (this.editingValue) {
                    await carbonApi.gwpProfile.updateValue(
                        this.selectedProfile.profileIdx,
                        this.editingValue.gwpValueIdx,
                        { gwpValue: this.valueForm.gwpValue, sourceRef: this.valueForm.sourceRef || null },
                    );
                    this.$bvToast?.toast?.("GWP 값이 수정되었습니다.", { variant: "success" });
                } else {
                    await carbonApi.gwpProfile.addValue(this.selectedProfile.profileIdx, {
                        gasCode: this.valueForm.gasCode,
                        gwpValue: this.valueForm.gwpValue,
                        sourceRef: this.valueForm.sourceRef || null,
                    });
                    this.$bvToast?.toast?.("GWP 값이 추가되었습니다.", { variant: "success" });
                }
                this.showValueFormModal = false;
                this.editingValue = null;
                await this.loadValues();
            } catch (e) {
                const msg = e?.response?.data?.message ?? e?.message ?? "저장에 실패했습니다.";
                this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
            }
        },
        async deleteValue(value) {
            if (!this.selectedProfile || !confirm("이 GWP 값을 삭제하시겠습니까?")) return;
            try {
                await carbonApi.gwpProfile.deleteValue(this.selectedProfile.profileIdx, value.gwpValueIdx);
                this.$bvToast?.toast?.("삭제되었습니다.", { variant: "success" });
                await this.loadValues();
            } catch (e) {
                const msg = e?.response?.data?.message ?? e?.message ?? "삭제에 실패했습니다.";
                this.$bvToast?.toast?.(msg, { variant: "danger", title: "오류" });
            }
        },
    },
};
</script>

<style scoped>
.gwp-profile-manager {
    width: 100%;
}

.tab-content {
    flex: 1;
    min-height: 0;
}

/* table-compact/table-container-compact/empty-state/action-buttons 는 전역 _carbon-tables.scss 적용 */

.profile-code-cell {
    font-weight: 600;
}
.form-group {
    margin-bottom: 14px;
}
.form-group label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 4px;
}
.required {
    color: #dc2626;
}
.checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: normal;
}
.values-panel {
    padding: 0;
}

.values-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.values-profile-label {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
}

.values-bar .icon-btn-compact {
    padding: 0;
    background: transparent;
    border: 1px solid #ced4da;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    color: #475569;
}

.values-bar .icon-btn-compact:hover {
    background: #f0f9f4;
    border-color: #52b788;
    color: #1e293b;
}

.values-bar .icon-btn-compact i {
    font-size: 13px;
}

.values-table-wrapper {
    max-height: 360px;
}

.modal-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.modal-detail-header .modal-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
}

.modal-detail-header .close {
    padding: 0;
    margin: 0;
    background: transparent;
    border: none;
    font-size: 1.5rem;
    color: #64748b;
    cursor: pointer;
    line-height: 1;
}

.modal-detail-header .close:hover {
    color: #2d8659;
}
</style>
